import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Button from '../Button/Button';
import './DownloadMenu.css';

const DownloadMenu = ({
  onDownload,
  disabled = false,
  busy = false,
  label = 'Download',
  busyLabel = 'Exporting...',
  placement = 'bottom',
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: null, bottom: null, left: 0 });
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = useCallback(() => {
    const btn = wrapperRef.current?.querySelector('.download-menu-button');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    if (placement === 'top') {
      setMenuPos({
        top: null,
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left
      });
    } else {
      setMenuPos({
        top: rect.bottom + 4,
        bottom: null,
        left: rect.left
      });
    }
  }, [placement]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleReposition = () => updatePosition();
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  const handleToggle = () => {
    if (disabled || busy) return;
    setOpen(prev => !prev);
  };

  const handleChoose = (format) => {
    setOpen(false);
    onDownload(format);
  };

  return (
    <div ref={wrapperRef} className={`download-menu-wrapper ${className}`}>
      <Button
        variant="outline"
        size="small"
        className="download-menu-button"
        onClick={handleToggle}
        disabled={disabled || busy}
      >
        {busy ? busyLabel : label}
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} download-menu-chevron`}></i>
      </Button>
      {open && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="download-menu"
          role="menu"
          style={{
            ...(menuPos.top != null ? { top: `${menuPos.top}px` } : {}),
            ...(menuPos.bottom != null ? { bottom: `${menuPos.bottom}px` } : {}),
            left: `${menuPos.left}px`
          }}
        >
          <button
            type="button"
            role="menuitem"
            className="download-menu-item"
            onClick={() => handleChoose('csv')}
          >
            CSV
          </button>
          <button
            type="button"
            role="menuitem"
            className="download-menu-item"
            onClick={() => handleChoose('json')}
          >
            JSON
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DownloadMenu;
